import React, { useState, useEffect } from "react";
import { useI18n } from "../../lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { projects as projectsApi } from "../../lib/api";
import { Share, Copy, ArrowSquareOut, Camera, GlobeHemisphereEast, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ShareDialog({ open, onOpenChange, project, onProjectChange }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [isPublic, setIsPublic] = useState(!!project.is_public);
  const [slug, setSlug] = useState(project.slug);

  useEffect(() => {
    setIsPublic(!!project.is_public);
    setSlug(project.slug);
  }, [project]);

  const publicUrl = slug ? `${window.location.origin}/showcase/${slug}` : "";

  const toggle = async (val) => {
    setBusy(true);
    try {
      const res = await projectsApi.share(project.project_id, val);
      setIsPublic(res.is_public);
      setSlug(res.slug);
      onProjectChange?.({ is_public: res.is_public, slug: res.slug });
      toast.success(val ? t("toast.published") : t("toast.unpublished"));
    } catch {
      toast.error(t("toast.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("toast.linkCopied"));
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none text-zinc-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight font-light flex items-center gap-2">
            <Share size={20} weight="duotone" className="text-cyan-400" /> {t("ide.share.title")}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400 leading-relaxed">
            {t("ide.share.desc")}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-zinc-800 bg-zinc-900 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isPublic ? <GlobeHemisphereEast size={18} className="text-cyan-400" /> : <Lock size={18} className="text-zinc-500" />}
            <div>
              <div className="text-xs font-mono">{isPublic ? t("ide.share.public") : t("ide.share.private")}</div>
              <div className="text-[10px] text-zinc-500 tracking-[0.2em] uppercase">
                {isPublic ? "anyone with link" : "owner only"}
              </div>
            </div>
          </div>
          <Switch
            data-testid="share-toggle"
            checked={isPublic}
            disabled={busy}
            onCheckedChange={toggle}
            className="data-[state=checked]:bg-cyan-400"
          />
        </div>

        {isPublic && (
          <div className="space-y-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{t("ide.share.url")}</div>
            <div className="flex items-stretch gap-1 border border-zinc-800">
              <div data-testid="share-url" className="flex-1 px-3 py-2 text-xs font-mono text-cyan-300 truncate bg-zinc-900">
                {publicUrl}
              </div>
              <button
                data-testid="share-copy-btn"
                onClick={copyLink}
                className="px-3 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-cyan-400 transition-colors"
                title={t("ide.share.copy")}
              >
                <Copy size={14} />
              </button>
              <a
                data-testid="share-open-btn"
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="px-3 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-cyan-400 transition-colors flex items-center"
                title={t("ide.share.viewPublic")}
              >
                <ArrowSquareOut size={14} />
              </a>
            </div>
            <p className="text-[10px] text-zinc-500 leading-relaxed pt-1">
              <Camera size={12} className="inline mr-1 text-cyan-400" />
              {t("ide.share.thumbnail")}
            </p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={() => onOpenChange(false)} variant="ghost" className="rounded-none text-zinc-300 hover:text-zinc-50">
            {t("common.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
